import type React from 'react'
import { useState } from 'react'
import { ArrowDownIcon, ArrowUpIcon, FunnelIcon } from '@heroicons/react/24/outline'
import type { Column, FilterType } from './OtuDataTableView.logic.js'
import { taxonFilterType } from './OtuDataTableView.logic.js'
import { AutocompleteFilterInput } from './AutocompleteFilterInput.js'

// Re-exported for existing importers (AutocompleteFilterInput). Defined in OtuDataTableView.logic.
export type { FilterType }

interface SortableTableHeaderProps {
  column: Column
  sortField: string
  sortDirection: 'ASC' | 'DESC'
  isFiltered: (field: string) => boolean
  handleSort: (field: string) => void
  handleFilterClick: (field: string, e: React.MouseEvent) => void
  filterDialogOpen?: string | null
  currentValue?: string
  applyFilter?: (field: string, value: string, filterType?: FilterType) => void
  clearFilter?: (field: string) => void
  id: string
  type: 'ORDER' | 'PROJECT'
  selectedBlastType: string
}

export const SortableTableHeader: React.FC<SortableTableHeaderProps> = ({
  column,
  sortField,
  sortDirection,
  isFiltered,
  handleSort,
  handleFilterClick,
  filterDialogOpen,
  currentValue,
  applyFilter,
  clearFilter,
  id,
  type,
  selectedBlastType
}) => {
  const [selectedFilterType, setSelectedFilterType] = useState<FilterType>(column.isNumber ? 'EQUAL' : 'LIKE')
  const [inputValue, setInputValue] = useState(currentValue || '')
  const isNumberInvalid = column.isNumber && isNaN(Number(inputValue.trim()))
  const isApplyDisabled = inputValue.trim() === '' || (column.isNumber && isNumberInvalid)

  // Filter type options for number columns
  const numberFilterOptions: { value: FilterType; label: string }[] = [
    { value: 'EQUAL', label: '=' },
    { value: 'LESS_THAN', label: '<' },
    { value: 'LESS_THAN_OR_EQUAL', label: '≤' },
    { value: 'GREATER_THAN', label: '>' },
    { value: 'GREATER_THAN_OR_EQUAL', label: '≥' }
  ]

  return (
    <th
      data-testid={`otu-col-${column.field}`}
      title={column.title}
      className={(column.sortable ? 'cursor-pointer ' : '') + 'align-bottom'}
      onClick={() => (column.sortable ? handleSort(column.field) : {})}
    >
      {column.verticalHeader ? (
        <span
          className="inline-block max-h-32 overflow-hidden text-ellipsis whitespace-nowrap"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          title={column.label}
        >
          {column.headerLabel ?? column.label}
        </span>
      ) : (
        column.label
      )}
      {column.sortable &&
        sortField === column.field &&
        (sortDirection === 'ASC' ? <ArrowUpIcon className="h-4 w-4 inline ml-1" /> : <ArrowDownIcon className="h-4 w-4 inline ml-1" />)}
      {column.filterable && (
        <FunnelIcon
          data-testid={`otu-filter-${column.field}`}
          className={`h-4 w-4 inline ml-1 cursor-pointer ${isFiltered(column.field) ? 'text-blue-500' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            handleFilterClick(column.field, e)
          }}
        />
      )}
      {filterDialogOpen === column.field && applyFilter && clearFilter && (
        <div
          className="absolute bg-white p-2 shadow-lg rounded-sm z-10"
          onClick={(e) => e.stopPropagation()} // Stop propagation to prevent triggering the header's onClick
        >
          {column.isNumber && (
            <div className="mb-2">
              <select
                data-testid="otu-filter-operator"
                className="select select-bordered select-sm w-full"
                value={selectedFilterType}
                onChange={(e) => {
                  e.stopPropagation() // Stop propagation
                  setSelectedFilterType(e.target.value as FilterType)
                }}
              >
                {numberFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!column.isNumber ? (
            <AutocompleteFilterInput
              column={column}
              applyFilter={applyFilter}
              id={id}
              type={type}
              blastType={selectedBlastType}
              query={currentValue || ''}
              onSelect={(value) => {
                applyFilter(column.field, value, taxonFilterType(true))
              }}
            />
          ) : (
            <input
              type="text"
              data-testid="otu-filter-input"
              className="input input-bordered input-sm"
              value={inputValue}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation()
                setInputValue(e.target.value)
              }}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e.key === 'Enter' && !isApplyDisabled) {
                  console.log('key down for enter')
                  applyFilter(column.field, e.currentTarget.value, selectedFilterType)
                }
              }}
            />
          )}
          <div className="flex justify-between mt-2">
            <button
              data-testid="otu-filter-clear"
              className="btn btn-xs"
              onClick={(e) => {
                e.stopPropagation() // Stop propagation
                clearFilter(column.field)
              }}
            >
              Clear
            </button>
            <button
              disabled={isApplyDisabled}
              data-testid="otu-filter-apply"
              className="btn btn-xs btn-primary"
              onClick={(e) => {
                console.info('selected filter is ', selectedFilterType, column)
                e.stopPropagation() // Stop propagation
                applyFilter(column.field, inputValue, column.isNumber ? selectedFilterType : 'LIKE')
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </th>
  )
}
